import "./CompanySales.css";
import SaleReturnModal from "./components/SaleReturnModal";
import JournalAuditModal from "./components/JournalAuditModal";
import { usePermissions } from "./PermissionContext";

import {
  openSaleReceiptPrintWindow,
  printSaleReceipt,
} from "./printing/SaleReceiptPrinter";

import {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import { toast } from "react-toastify";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

const API_URL = "/api";

const PAYMENT_METHODS = [
  { value: "cash", label: "Efectivo" },
  { value: "card", label: "Tarjeta" },
  { value: "transfer", label: "Transferencia" },
  { value: "credit", label: "Crédito" },
];

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

const getProductId = (product) =>
  product.id ?? product.product_id ?? product.product?.id;

const getProductStock = (product) =>
  Number(product.current_stock ?? 0);

const getProductPrice = (product) =>
  Number(
    product.sale_price ??
    product.precio_venta ??
    product.price ??
    product.product?.sale_price ??
    0
  );

const getCartKey = (
  productId,
  variantId = null
) => `${productId}:${variantId ?? "base"}`;

const getVariantPrice = (
  product,
  variant
) =>
  Number(
    variant.sale_price ??
    getProductPrice(product)
  );

const getResponseItems = (data) => {
  if (Array.isArray(data)) return data;

  return data?.items ?? data?.products ?? data?.data ?? [];
};

const normalizeCashSession = (data) => {
  const session =
    data?.session ??
    data?.current_session ??
    data?.cash_session ??
    data?.data ??
    data;

  if (!session) return null;

  const status = String(session.status ?? "").toLowerCase();

  const isClosed =
    session.is_open === false ||
    status === "closed" ||
    status === "cerrada" ||
    status === "close";

  if (isClosed) return null;

  const sessionId =
    session.id ?? session.cash_session_id ?? session.session_id;

  if (!sessionId) return null;

  return {
    ...session,
    id: sessionId,
    opening_amount:
      session.opening_amount ??
      session.initial_amount ??
      session.opening_balance ??
      session.amount_opening ??
      0,
  };
};

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "Ocurrió un error. Intenta nuevamente.";
};

function CompanySales() {
  const barcodeInputRef = useRef(null);
  const keyboardActionsRef = useRef({
    createSale: null,
    clearCart: null,
  });

  const navigate = useNavigate();
  const { company } = useParams();
  const { can } = usePermissions();

  const canCreateSales = can("sales.create");
  const canCancelSales = can("sales.cancel");
  const canViewAccounting = can("accounting.view");
  const canViewAudit = can("accounting.audit");

  const [promotionPreview, setPromotionPreview] = useState(null);

  const [loadingPromotionPreview, setLoadingPromotionPreview] =
    useState(false);

  const [promotionPreviewError, setPromotionPreviewError] =
    useState(null);

  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [payments, setPayments] = useState([
    {
      payment_method: "cash",
      amount: 0,
      received_amount: 0,
    },
  ]);

  const [search, setSearch] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [savingSale, setSavingSale] = useState(false);
  const [saleModal, setSaleModal] = useState(null);
  const [variantPicker, setVariantPicker] = useState(null);

  const [loadingVariants, setLoadingVariants] =
    useState(false);

  const [cashSession, setCashSession] = useState(null);
  const [recentSales, setRecentSales] = useState([]);

  const [customerName, setCustomerName] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] =
    useState(false);

  const [customerId, setCustomerId] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerMatches, setShowCustomerMatches] =
    useState(false);
  const [creditDueDate, setCreditDueDate] = useState("");
  const [message, setMessage] = useState(null);
  const [linkedJournalEntry, setLinkedJournalEntry] =
    useState(null);
  const [auditJournalEntry, setAuditJournalEntry] =
    useState(null);



  const subtotal = cart.reduce(
    (sum, item) =>
      sum +
      Number(item.sale_price || 0) *
      Number(item.quantity || 0),
    0
  );

  const discount = Number(
    promotionPreview?.total_discount ?? 0
  );

  const total = Number(
    promotionPreview?.total ?? subtotal
  );

  const paidTotal = payments.reduce(
    (sum, payment) => sum + Number(payment.amount || 0),
    0
  );

  const pendingAmount = total - paidTotal;

  const hasCreditPayment = payments.some(
    (payment) =>
      payment.payment_method === "credit"
  );

  const creditAmount = payments
    .filter(
      (payment) =>
        payment.payment_method === "credit"
    )
    .reduce(
      (sum, payment) =>
        sum + Number(payment.amount || 0),
      0
    );

  const cashAppliedTotal = payments
    .filter(
      (payment) =>
        payment.payment_method === "cash"
    )
    .reduce(
      (sum, payment) =>
        sum + Number(payment.amount || 0),
      0
    );

  const cashReceivedTotal = payments
    .filter(
      (payment) =>
        payment.payment_method === "cash"
    )
    .reduce(
      (sum, payment) =>
        sum +
        Number(
          payment.received_amount ??
          payment.amount ??
          0
        ),
      0
    );

  const changeAmount = Math.max(
    0,
    cashReceivedTotal - cashAppliedTotal
  );

  const cashAmountPending = Math.max(
    0,
    cashAppliedTotal - cashReceivedTotal
  );

  const loadCashSession = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/cash-register/me/current`,
        {
          headers: getHeaders(),
        }
      );

      const normalizedSession = normalizeCashSession(response.data);

      setCashSession(normalizedSession);
    } catch (error) {
      setCashSession(null);

      setMessage({
        type: "error",
        text: `No fue posible consultar la caja: ${getErrorMessage(error)}`,
      });
    }
  }, []);

  const loadProducts = useCallback(async (searchText = "") => {
    setLoadingProducts(true);

    try {
      const response = await axios.get(`${API_URL}/inventory/me`, {
        headers: getHeaders(),
        params: {
          search: searchText,
          limit: 50,
          offset: 0,
        },
      });

      setProducts(getResponseItems(response.data));
    } catch (error) {
      setProducts([]);

      setMessage({
        type: "error",
        text: `No fue posible cargar productos: ${getErrorMessage(error)}`,
      });
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const loadRecentSales = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/sales/me`, {
        headers: getHeaders(),
        params: {
          limit: 8,
        },
      });

      setRecentSales(getResponseItems(response.data));
    } catch (error) {
      setRecentSales([]);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    setLoadingCustomers(true);

    try {
      const response = await axios.get(
        `${API_URL}/customers/me`,
        {
          headers: getHeaders(),
          params: {
            active_only: true,
          },
        }
      );

      setCustomers(getResponseItems(response.data));
    } catch (error) {
      setCustomers([]);

      console.error(
        "No fue posible cargar clientes:",
        error
      );
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  const normalizeCustomerText = (value) =>
    String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const matchingCustomers = useMemo(() => {
    const term = normalizeCustomerText(customerSearch);

    if (!term) return [];

    return customers
      .filter((customer) =>
        normalizeCustomerText(
          `${customer.document_number} ${customer.name}`
        ).includes(term)
      )
      .slice(0, 8);
  }, [customers, customerSearch]);

  const handleCustomerSearchChange = (event) => {
    setCustomerSearch(event.target.value);
    setCustomerId("");
    setCustomerName("");
    setShowCustomerMatches(true);
  };

  const selectCustomer = (customer) => {
    setCustomerId(String(customer.id));
    setCustomerName(customer.name);
    setCustomerSearch(
      `${customer.document_number} - ${customer.name}`
    );
    setShowCustomerMatches(false);
  };

  const loadInitialData = useCallback(async () => {
    await Promise.all([
      loadCashSession(),
      loadProducts(),
      loadRecentSales(),
      loadCustomers(),
    ]);
  }, [loadCashSession, loadProducts, loadRecentSales, loadCustomers,]);

  useEffect(() => {
    loadInitialData();

    const timer = setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 250);

    return () => clearTimeout(timer);
  }, [loadInitialData]);

  useEffect(() => {
    if (!message) {
      return;
    }

    if (message.type === "success") {
      toast.success(message.text);
    } else {
      toast.error(message.text);
    }

    setMessage(null);
  }, [message]);


  useEffect(() => {
    setPayments((currentPayments) => {
      if (currentPayments.length !== 1) {
        return currentPayments;
      }

      const payment = currentPayments[0];
      const amount = Number(total.toFixed(2));

      const previousAmount = Number(
        payment.amount || 0
      );

      const previousReceived = Number(
        payment.received_amount || 0
      );

      const shouldSyncReceived =
        previousReceived === 0 ||
        previousReceived === previousAmount;

      return [
        {
          ...payment,
          amount,
          received_amount:
            payment.payment_method === "cash" &&
              shouldSyncReceived
              ? amount
              : previousReceived,
        },
      ];
    });
  }, [total, payments.length]);

  const addToCart = (
    product,
    variant = null
  ) => {
    const productId = getProductId(product);

    const availableStock = variant
      ? Number(variant.stock || 0)
      : getProductStock(product);

    const variantId = variant?.id ?? null;

    const cartKey = getCartKey(
      productId,
      variantId
    );

    const productName =
      product.name ??
      product.product_name ??
      "Producto";

    const itemName = variant
      ? `${productName} · ${variant.name}`
      : productName;

    if (!productId) {
      setMessage({
        type: "error",
        text: "El producto no tiene un identificador válido.",
      });
      return;
    }

    if (availableStock <= 0) {
      setMessage({
        type: "error",
        text: `No hay existencias disponibles para "${itemName}".`,
      });
      return;
    }

    setCart((currentCart) => {
      const existingItem = currentCart.find(
        (item) => item.cart_key === cartKey
      );

      if (existingItem) {
        if (
          Number(existingItem.quantity) >=
          availableStock
        ) {
          setMessage({
            type: "error",
            text: `No puedes agregar más unidades de ${itemName}.`,
          });

          return currentCart;
        }

        return currentCart.map((item) =>
          item.cart_key === cartKey
            ? {
              ...item,
              quantity: Number(item.quantity) + 1,
            }
            : item
        );
      }

      return [
        ...currentCart,
        {
          cart_key: cartKey,
          id: productId,
          variant_id: variantId,
          variant_name: variant?.name ?? null,
          sku: variant?.sku ??
            product.sku ??
            product.code ??
            "Sin código",
          name: itemName,
          category: product.category ?? "Sin categoría",
          sale_price: variant
            ? getVariantPrice(product, variant)
            : getProductPrice(product),
          stock_actual: availableStock,
          quantity: 1,
        },
      ];
    });

    setMessage(null);
  };

  const addInventoryVariantToCart = (inventoryItem) => {
    if (!inventoryItem?.variant_id) {
      setMessage({
        type: "error",
        text: "La variante no tiene un identificador válido.",
      });
      return;
    }

    const parentProduct = {
      ...inventoryItem,
      id:
        inventoryItem.parent_product_id ??
        inventoryItem.id,
      name:
        inventoryItem.parent_product_name ??
        inventoryItem.name,
    };

    const variant = {
      id: inventoryItem.variant_id,
      name:
        inventoryItem.variant_name ??
        inventoryItem.name,
      sku:
        inventoryItem.variant_sku ??
        inventoryItem.sku,
      sale_price: inventoryItem.sale_price,
      stock: inventoryItem.current_stock,
    };

    addToCart(parentProduct, variant);
  };

  const handleProductSelection = async (product) => {
    const productId = getProductId(product);

    if (!productId) {
      setMessage({
        type: "error",
        text: "El producto no tiene un identificador válido.",
      });
      return;
    }

    try {
      setLoadingVariants(true);

      const response = await axios.get(
        `${API_URL}/products/me/${productId}/variants`,
        {
          headers: getHeaders(),
          params: {
            only_active: true,
          },
        }
      );

      const variants = response.data || [];

      if (variants.length === 0) {
        addToCart(product);
        return;
      }

      setVariantPicker({
        product,
        variants,
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: (
          "No fue posible consultar las variantes: " +
          getErrorMessage(error)
        ),
      });
    } finally {
      setLoadingVariants(false);
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();

    const searchValue = search.trim();

    if (!searchValue) {
      await loadProducts();
      return;
    }

    setLoadingProducts(true);

    try {
      const response = await axios.get(`${API_URL}/inventory/me`, {
        headers: getHeaders(),
        params: {
          search: searchValue,
          limit: 50,
          offset: 0,
        },
      });

      const foundProducts = getResponseItems(response.data);

      setProducts(foundProducts);

      const exactProduct = foundProducts.find((product) => {
        const sku = String(product.sku ?? product.code ?? "")
          .trim()
          .toLowerCase();

        return sku === searchValue.toLowerCase();
      });

      if (exactProduct) {
        if (
          exactProduct.is_variant &&
          exactProduct.variant_id
        ) {
          addInventoryVariantToCart(exactProduct);
        } else {
          await handleProductSelection(exactProduct);
        }

        setSearch("");
        barcodeInputRef.current?.focus();
        return;
      }

      if (foundProducts.length === 0) {
        setMessage({
          type: "error",
          text: "No se encontró un producto con ese código o nombre.",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: getErrorMessage(error),
      });
    } finally {
      setLoadingProducts(false);
    }
  };

  const updateQuantity = (cartKey, value) => {
    const quantity = Number(value);

    setCart((currentCart) =>
      currentCart
        .map((item) => {
          if (item.cart_key !== cartKey) {
            return item;
          }

          if (quantity <= 0) {
            return null;
          }

          if (quantity > Number(item.stock_actual)) {
            setMessage({
              type: "error",
              text: (
                "Stock máximo disponible: " +
                `${item.stock_actual}.`
              ),
            });

            return item;
          }

          return {
            ...item,
            quantity,
          };
        })
        .filter(Boolean)
    );
  };

  const removeFromCart = (cartKey) => {
    setCart((currentCart) =>
      currentCart.filter(
        (item) => item.cart_key !== cartKey
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    setCustomerName("");
    setCustomerId("");
    setCreditDueDate("");
    setPayments([
      {
        payment_method: "cash",
        amount: 0,
        received_amount: 0,
      },
    ]);
    setMessage(null);

    barcodeInputRef.current?.focus();
  };

  const addPayment = () => {
    setPayments((currentPayments) => [
      ...currentPayments,
      {
        payment_method: "card",
        amount: 0,
        received_amount: 0,
      },
    ]);
  };

  const updatePayment = (index, field, value) => {
    setPayments((currentPayments) =>
      currentPayments.map((payment, paymentIndex) => {
        if (paymentIndex !== index) {
          return payment;
        }

        if (field === "payment_method") {
          return {
            ...payment,
            payment_method: value,
            received_amount:
              value === "cash"
                ? Number(
                  payment.received_amount ||
                  payment.amount ||
                  0
                )
                : Number(payment.amount || 0),
          };
        }

        const numericValue = Number(value || 0);

        if (field === "amount") {
          const previousAmount = Number(
            payment.amount || 0
          );

          const previousReceived = Number(
            payment.received_amount || 0
          );

          const shouldSyncReceived =
            payment.payment_method === "cash" &&
            (
              previousReceived === 0 ||
              previousReceived === previousAmount
            );

          return {
            ...payment,
            amount: numericValue,
            received_amount: shouldSyncReceived
              ? numericValue
              : previousReceived,
          };
        }

        if (field === "received_amount") {
          return {
            ...payment,
            received_amount: numericValue,
          };
        }

        return payment;
      })
    );
  };

  const removePayment = (index) => {
    if (payments.length === 1) return;

    setPayments((currentPayments) =>
      currentPayments.filter((_, paymentIndex) => paymentIndex !== index)
    );
  };

  const selectMainPaymentMethod = (method) => {
    setPayments([
      {
        payment_method: method,
        amount: Number(total.toFixed(2)),
        received_amount: Number(total.toFixed(2)),
      },
    ]);
  };

  const handleCreateSale = async () => {
    if (!canCreateSales) {
      toast.error(
        "No tienes permiso para registrar ventas."
      );
      return;
    }

    setMessage(null);

    if (!cashSession?.id) {
      setMessage({
        type: "error",
        text: "Debes abrir una caja antes de registrar ventas.",
      });
      return;
    }

    if (cart.length === 0) {
      setMessage({
        type: "error",
        text: "Agrega al menos un producto al carrito.",
      });
      return;
    }

    if (
      loadingPromotionPreview ||
      !promotionPreview ||
      promotionPreviewError
    ) {
      setMessage({
        type: "error",
        text: promotionPreviewError
          ? `No fue posible actualizar promociones: ${promotionPreviewError}`
          : "Espera mientras se actualizan los precios y promociones.",
      });
      return;
    }

    if (
      payments.some(
        (payment) => Number(payment.amount) <= 0
      )
    ) {
      setMessage({
        type: "error",
        text: "Cada pago debe tener un valor mayor a cero.",
      });
      return;
    }
    if (creditAmount > 0 && !customerId) {
      setMessage({
        type: "error",
        text: (
          "Para registrar una venta a crédito debes "
          + "seleccionar un cliente."
        ),
      });
      return;
    }

    if (creditAmount > 0 && !creditDueDate) {
      setMessage({
        type: "error",
        text: (
          "Indica la fecha de vencimiento para el crédito."
        ),
      });
      return;
    }
    if (cashAmountPending > 0.01) {
      setMessage({
        type: "error",
        text: `Faltan ${money(
          cashAmountPending
        )} por recibir en efectivo.`,
      });
      return;
    }

    if (Math.abs(paidTotal - total) > 0.01) {
      setMessage({
        type: "error",
        text: `El total de pagos debe ser igual a ${money(
          total
        )}.`,
      });
      return;
    }

    const payload = {
      customer_id: customerId
        ? Number(customerId)
        : null,
      customer_name: customerName.trim() || null,
      credit_due_date:
        creditAmount > 0
          ? creditDueDate || null
          : null,
      items: cart.map((item) => ({
        product_id: item.id,
        variant_id: item.variant_id || null,
        quantity: Number(item.quantity),
      })),
      payments: payments.map((payment) => ({
        payment_method: payment.payment_method,
        amount: Number(payment.amount),
      })),
    };

    /*
      Debe abrirse antes del await para que el navegador
      no bloquee la impresión automática.
    */
    const printWindow = openSaleReceiptPrintWindow();

    setSavingSale(true);

    try {
      const saleResponse = await axios.post(
        `${API_URL}/sales/me`,
        payload,
        {
          headers: getHeaders(),
        }
      );

      const saleId = saleResponse.data.id;

      const saleReference =
        saleResponse.data.document_number || `#${saleId}`;

      setMessage({
        type: "success",
        text: `Venta ${saleReference} registrada correctamente.`,
      });

      /*
        La venta ya fue registrada. Si falla la impresión,
        nunca se debe mostrar como si la venta hubiera fallado.
      */
      try {
        const receiptResponse = await axios.get(
          `${API_URL}/sales/${saleId}/receipt`,
          {
            headers: getHeaders(),
          }
        );

        printSaleReceipt(
          receiptResponse.data,
          printWindow
        );
      } catch (printError) {
        printWindow?.close();

        toast.warning(
          "La venta fue registrada, pero no fue posible generar el recibo."
        );

        console.error(printError);
      }

      setCart([]);
      setCustomerName("");
      setPayments([
        {
          payment_method: "cash",
          amount: 0,
          received_amount: 0,
        },
      ]);

      try {
        await Promise.all([
          loadCashSession(),
          loadProducts(),
          loadRecentSales(),
        ]);
      } catch (refreshError) {
        console.error(refreshError);
      }

      window.setTimeout(() => {
        barcodeInputRef.current?.focus();
      }, 100);
    } catch (error) {
      printWindow?.close();

      setMessage({
        type: "error",
        text: getErrorMessage(error),
      });
    } finally {
      setSavingSale(false);
    }
  };

  const getPaymentLabel = (method) =>
    PAYMENT_METHODS.find((item) => item.value === method)?.label || method;
  const getSaleStatusLabel = (status) => {
    const labels = {
      completed: "Completada",
      partially_refunded: "Reintegro parcial",
      cancelled: "Anulada",
    };

    return labels[status] || status;
  };

  const openSaleModal = async (saleId, mode) => {
    try {
      const response = await axios.get(
        `${API_URL}/sales/${saleId}`,
        {
          headers: getHeaders(),
        }
      );

      setSaleModal({
        sale: response.data,
        mode,
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: getErrorMessage(error),
      });
    }
  };

  const handleViewJournalEntry = async (sale) => {
    try {
      const response = await axios.get(
        `${API_URL}/accounting/source-entry/sale/${sale.id}`,
        {
          headers: getHeaders(),
        }
      );

      setLinkedJournalEntry(response.data);
    } catch (error) {
      const status = error.response?.status;

      if (status === 404) {
        toast.info(
          "Esta venta todavía no tiene un comprobante contable asociado."
        );
        return;
      }

      toast.error(getErrorMessage(error));
    }
  };
  keyboardActionsRef.current = {
    createSale: handleCreateSale,
    clearCart,
  };
  useEffect(() => {
    const handleKeyboardShortcuts = (event) => {
      const tagName =
        document.activeElement?.tagName?.toLowerCase();

      if (
        ["input", "textarea", "select"].includes(tagName) &&
        event.key !== "F10"
      ) {
        return;
      }

      if (event.key === "F10") {
        event.preventDefault();
        keyboardActionsRef.current.createSale?.();
      }

      if (event.key === "F3") {
        event.preventDefault();
        keyboardActionsRef.current.clearCart?.();
      }

      if (event.key === "Escape") {
        setSaleModal(null);
        setLinkedJournalEntry(null);
        setAuditJournalEntry(null);
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyboardShortcuts
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyboardShortcuts
      );
    };
  }, []);

  useEffect(() => {
    if (!cart.length) {
      setPromotionPreview(null);
      setPromotionPreviewError(null);
      setLoadingPromotionPreview(false);

      return undefined;
    }

    let cancelled = false;

    /*
      Invalida de inmediato el cálculo anterior.
      Nunca se permite cobrar con precios de un carrito previo.
    */
    setPromotionPreview(null);
    setPromotionPreviewError(null);
    setLoadingPromotionPreview(true);

    const timer = window.setTimeout(async () => {
      try {
        const response = await axios.post(
          `${API_URL}/promotions/me/preview`,
          {
            items: cart.map((item) => ({
              product_id: item.id,
              variant_id: item.variant_id || null,
              quantity: item.quantity,
            })),
          },
          {
            headers: getHeaders(),
          }
        );

        if (!cancelled) {
          setPromotionPreview(response.data);
        }
      } catch (error) {
        if (!cancelled) {
          const errorMessage = getErrorMessage(error);

          setPromotionPreview(null);
          setPromotionPreviewError(errorMessage);

          console.error(
            "No fue posible cotizar promociones:",
            error.response?.data || error
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingPromotionPreview(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [cart]);

  const promotionByCartKey = useMemo(() => {
    const entries = promotionPreview?.items || [];

    return new Map(
      entries.map((item) => [
        getCartKey(
          item.product_id,
          item.variant_id
        ),
        item,
      ])
    );
  }, [promotionPreview]);

  return (
    <div className="company-sales">
      <section className="sales-header">
        <div>
          <p className="sales-kicker">Punto de venta</p>
          <h1>Ventas POS</h1>
          <p>Escanea el código, agrega productos y registra el pago.</p>
        </div>

        <div
          className={`cash-status ${cashSession?.id ? "cash-open" : "cash-closed"
            }`}
        >
          <span className="cash-status-dot" />

          <div>
            <strong>
              {cashSession?.id ? "Caja abierta" : "Caja cerrada"}
            </strong>

            <small>
              {cashSession?.id
                ? `Apertura: ${money(cashSession.opening_amount)}`
                : "Abre la caja antes de vender"}
            </small>
          </div>
        </div>
      </section>

      <div className="pos-layout pos-modern-layout">
        <section className="pos-catalog pos-modern-catalog">
          <form
            className="pos-search pos-modern-search"
            onSubmit={handleSearch}
          >
            <span className="pos-search-icon">▥</span>

            <input
              ref={barcodeInputRef}
              id="product-search"
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar producto por código de barras, SKU o nombre..."
              autoComplete="off"
            />

            <button type="submit" disabled={loadingProducts}>
              {loadingProducts ? "Buscando..." : "⌕ Buscar"}
            </button>
          </form>

          <section className="pos-cart-table-card">
            <div className="pos-cart-table-heading">
              <div>
                <span>Venta en curso</span>
                <h2>
                  {cart.length
                    ? `${cart.length} producto(s) en el carrito`
                    : "Carrito de venta"}
                </h2>
              </div>

              {cart.length > 0 && (
                <button
                  type="button"
                  className="btn-clear-cart"
                  onClick={clearCart}
                >
                  Vaciar carrito
                </button>
              )}
            </div>

            <div className="pos-cart-table-wrap">
              {cart.length === 0 ? (
                <div className="pos-empty-sale">
                  <span>🛒</span>
                  <strong>Tu venta está lista para comenzar</strong>
                  <p>
                    Busca un producto o selecciónalo en productos
                    frecuentes.
                  </p>
                </div>
              ) : (
                <table className="pos-cart-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Código</th>
                      <th>Producto</th>
                      <th>Cantidad</th>
                      <th>Precio unit.</th>
                      <th>Subtotal</th>
                      <th aria-label="Eliminar" />
                    </tr>
                  </thead>

                  <tbody>
                    {cart.map((item, index) => {
                      const promotion = promotionByCartKey.get(
                        item.cart_key
                      );

                      const baseUnitPrice = Number(
                        promotion?.base_unit_price ?? item.sale_price
                      );

                      const finalUnitPrice = Number(
                        promotion?.final_unit_price ?? item.sale_price
                      );

                      const lineTotal = Number(
                        promotion?.total ??
                        finalUnitPrice * Number(item.quantity || 0)
                      );

                      return (
                        <tr key={item.cart_key}>
                          <td>{index + 1}</td>
                          <td>{item.sku}</td>

                          <td>
                            <strong>{item.name}</strong>
                            <small>{item.category}</small>

                            {promotion?.promotion_name ? (
                              <div className="sale-promotion-detail">
                                <span className="sale-promotion-badge">
                                  {promotion.promotion_name}
                                </span>

                                <span className="sale-discount-value">
                                  Descuento: - {money(promotion.discount_total)}
                                </span>
                              </div>
                            ) : null}
                          </td>

                          <td>
                            <div className="quantity-control">
                              <button
                                type="button"
                                onClick={() =>
                                  updateQuantity(item.cart_key,
                                    item.quantity - 1
                                  )
                                }
                                aria-label={`Disminuir ${item.name}`}
                              >
                                −
                              </button>

                              <input
                                type="number"
                                min="1"
                                max={item.stock_actual}
                                value={item.quantity}
                                onChange={(event) =>
                                  updateQuantity(item.cart_key,
                                    event.target.value
                                  )
                                }
                              />

                              <button
                                type="button"
                                onClick={() =>
                                  updateQuantity(item.cart_key,
                                    item.quantity + 1
                                  )
                                }
                                aria-label={`Aumentar ${item.name}`}
                              >
                                +
                              </button>
                            </div>
                          </td>

                          <td>
                            {promotion?.promotion_name ? (
                              <div className="sale-price-with-promotion">
                                <del>{money(baseUnitPrice)}</del>
                                <strong>{money(finalUnitPrice)}</strong>
                              </div>
                            ) : (
                              money(finalUnitPrice)
                            )}
                          </td>

                          <td>
                            <b>{money(lineTotal)}</b>
                          </td>

                          <td>
                            <button
                              type="button"
                              className="btn-remove-item"
                              onClick={() => removeFromCart(item.cart_key)}
                              title={`Eliminar ${item.name}`}
                              aria-label={`Eliminar ${item.name}`}
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <section className="pos-frequent-products">
            <div className="catalog-heading">
              <div>
                <h2>Productos frecuentes</h2>
                <span>
                  Selecciona un producto para agregarlo a la venta.
                </span>
              </div>

              <button
                type="button"
                className="btn-refresh-products"
                onClick={() => {
                  setSearch("");
                  loadProducts();
                }}
              >
                ↻ Actualizar
              </button>
            </div>

            <div className="product-grid pos-frequent-grid">
              {loadingProducts ? (
                <p className="catalog-empty">
                  Cargando productos...
                </p>
              ) : products.length === 0 ? (
                <p className="catalog-empty">
                  No hay productos disponibles.
                </p>
              ) : (
                products.slice(0, 10).map((product) => {
                  const stock = getProductStock(product);
                  const productId = getProductId(product);
                  const productName =
                    product.name ??
                    product.product_name ??
                    "Producto";

                  return (
                    <button
                      type="button"
                      className="pos-product-card"
                      key={`${productId}:${product.variant_id ?? "base"}`}
                      onClick={() => {
                        if (product.is_variant && product.variant_id) {
                          addInventoryVariantToCart(product);
                          return;
                        }

                        handleProductSelection(product);
                      }}
                      disabled={loadingVariants}
                    >
                      <span className="pos-product-avatar">
                        {productName.charAt(0).toUpperCase()}
                      </span>

                      <strong>{productName}</strong>

                      <small>
                        {product.sku ??
                          product.code ??
                          "Sin código"}
                      </small>

                      <div className="pos-product-footer">
                        <b>{money(getProductPrice(product))}</b>
                        <span
                          className={
                            stock <= 0 ? "out-of-stock" : ""
                          }
                        >
                          Stock: {stock}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </section>
        </section>

        <aside className="pos-cart pos-modern-checkout">
          <div className="checkout-heading">
            <span>Resumen de la venta</span>
            <h2>Total a cobrar</h2>
          </div>

          <div className="customer-field customer-search-field">
            <label htmlFor="customer-search">
              Cliente registrado{" "}
              <span>
                {hasCreditPayment
                  ? "(obligatorio para crédito)"
                  : "(opcional)"}
              </span>
            </label>

            <div className="customer-search-box">
              <input
                id="customer-search"
                type="text"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={
                  showCustomerMatches &&
                  Boolean(customerSearch.trim())
                }
                aria-controls="customer-match-list"
                autoComplete="off"
                value={customerSearch}
                onChange={handleCustomerSearchChange}
                onFocus={() => setShowCustomerMatches(true)}
                onBlur={() => setShowCustomerMatches(false)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setShowCustomerMatches(false);
                  }

                  if (
                    event.key === "Enter" &&
                    showCustomerMatches &&
                    matchingCustomers.length > 0
                  ) {
                    event.preventDefault();
                    selectCustomer(matchingCustomers[0]);
                  }
                }}
                placeholder="Buscar por documento o nombre"
              />

              {showCustomerMatches &&
                customerSearch.trim() && (
                  <div
                    id="customer-match-list"
                    className="customer-search-results"
                    role="listbox"
                  >
                    {loadingCustomers ? (
                      <p>Cargando clientes...</p>
                    ) : matchingCustomers.length > 0 ? (
                      matchingCustomers.map((customer) => (
                        <button
                          key={customer.id}
                          type="button"
                          role="option"
                          aria-selected={
                            customerId === String(customer.id)
                          }
                          onMouseDown={(event) =>
                            event.preventDefault()
                          }
                          onClick={() => selectCustomer(customer)}
                        >
                          <strong>{customer.name}</strong>
                          <small>
                            {customer.document_number}
                          </small>
                        </button>
                      ))
                    ) : (
                      <p>No se encontraron clientes.</p>
                    )}
                  </div>
                )}
            </div>

            <small className="customer-search-hint">
              {!customerSearch.trim()
                ? "En blanco: Cliente general."
                : customerId
                  ? "Cliente registrado seleccionado."
                  : "Selecciona una coincidencia de la lista."}
            </small>
          </div>

          <div className="customer-field">
            <label htmlFor="customer-name">
              Nombre para el recibo <span>(opcional)</span>
            </label>

            <input
              id="customer-name"
              type="text"
              value={customerName}
              onChange={(event) =>
                setCustomerName(event.target.value)
              }
              placeholder="Cliente general"
              readOnly={Boolean(customerId)}
            />
          </div>

          {hasCreditPayment && (
            <div className="customer-field">
              <label htmlFor="credit-due-date">
                Fecha de vencimiento del crédito
              </label>

              <input
                id="credit-due-date"
                type="date"
                value={creditDueDate}
                min={
                  new Date()
                    .toISOString()
                    .split("T")[0]
                }
                onChange={(event) =>
                  setCreditDueDate(event.target.value)
                }
                required
              />

              <small>
                Se generará una cuenta por cobrar para el cliente
                seleccionado.
              </small>
            </div>
          )}

          <div className="checkout-totals">
            <div>
              <span>Subtotal</span>
              <strong>{money(subtotal)}</strong>
            </div>

            {discount > 0 ? (
              <div className="checkout-discount">
                <span>Descuentos</span>
                <strong>- {money(discount)}</strong>
              </div>
            ) : null}

            <div className="checkout-total-payable">
              <span>Total a pagar</span>
              <strong>{money(total)}</strong>
            </div>

            {loadingPromotionPreview ? (
              <small className="promotion-loading">
                Actualizando promociones…
              </small>
            ) : null}
            {promotionPreviewError ? (
              <small className="promotion-error">
                No fue posible calcular promociones: {promotionPreviewError}
              </small>
            ) : null}
          </div>

          <section className="pos-payment-methods">
            <h3>Método de pago</h3>

            <div className="payment-method-buttons">
              {PAYMENT_METHODS.map((method) => (
                <button
                  type="button"
                  key={method.value}
                  className={
                    payments.length === 1 &&
                      payments[0].payment_method === method.value
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    selectMainPaymentMethod(method.value)
                  }
                >
                  <span>
                    {method.value === "cash"
                      ? "💵"
                      : method.value === "card"
                        ? "💳"
                        : method.value === "transfer"
                          ? "↔"
                          : "📒"}
                  </span>
                  {method.label}
                </button>
              ))}
            </div>
          </section>

          <section className="payment-section pos-modern-payment-section">
            <div className="payment-heading">
              <div>
                <span>Pago recibido</span>
                <h3>
                  {payments.length > 1
                    ? "Pago mixto"
                    : getPaymentLabel(
                      payments[0]?.payment_method
                    )}
                </h3>
              </div>

              <button type="button" onClick={addPayment}>
                + Pago mixto
              </button>
            </div>

            <div className="payment-list">
              {payments.map((payment, index) => (
                <div className="payment-row" key={index}>
                  <div className="payment-control">
                    <label>Medio de pago</label>

                    <select
                      value={payment.payment_method}
                      onChange={(event) =>
                        updatePayment(
                          index,
                          "payment_method",
                          event.target.value
                        )
                      }
                    >
                      {PAYMENT_METHODS.map((method) => (
                        <option
                          key={method.value}
                          value={method.value}
                        >
                          {method.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="payment-control">
                    <label>Valor aplicado</label>

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={payment.amount}
                      onChange={(event) =>
                        updatePayment(
                          index,
                          "amount",
                          event.target.value
                        )
                      }
                    />
                  </div>

                  {payment.payment_method === "cash" && (
                    <div className="payment-control payment-received">
                      <label>Cliente entrega</label>

                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={payment.received_amount}
                        onChange={(event) =>
                          updatePayment(
                            index,
                            "received_amount",
                            event.target.value
                          )
                        }
                      />
                    </div>
                  )}

                  {payments.length > 1 && (
                    <button
                      type="button"
                      className="btn-remove-payment"
                      onClick={() => removePayment(index)}
                      title="Eliminar medio de pago"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>

          <div className="payment-checkout-summary">
            <div>
              <span>Total aplicado</span>
              <strong>{money(paidTotal)}</strong>
            </div>

            {cashAppliedTotal > 0 && (
              <>
                <div>
                  <span>Recibido en efectivo</span>
                  <strong>{money(cashReceivedTotal)}</strong>
                </div>

                <div className="payment-change">
                  <span>Vuelto a entregar</span>
                  <strong>{money(changeAmount)}</strong>
                </div>
              </>
            )}
          </div>

          <p
            className={`payment-difference ${Math.abs(pendingAmount) <= 0.01 &&
              cashAmountPending <= 0.01
              ? "balanced"
              : ""
              }`}
          >
            {Math.abs(pendingAmount) <= 0.01 &&
              cashAmountPending <= 0.01
              ? "✓ Pago completo"
              : pendingAmount > 0
                ? `Falta por aplicar: ${money(pendingAmount)}`
                : pendingAmount < 0
                  ? `Excede el total: ${money(
                    Math.abs(pendingAmount)
                  )}`
                  : `Faltan ${money(
                    cashAmountPending
                  )} en efectivo`}
          </p>

          {canCreateSales && (
            <button
              type="button"
              className="btn-charge"
              onClick={handleCreateSale}
              disabled={
                savingSale ||
                loadingPromotionPreview ||
                !promotionPreview ||
                Boolean(promotionPreviewError) ||
                cart.length === 0 ||
                !cashSession?.id
              }
            >
              {savingSale
                ? "Procesando venta..."
                : `🛒 Cobrar ${money(total)} (F10)`}
            </button>
          )}

          <div className="checkout-secondary-actions">
            <button
              type="button"
              onClick={clearCart}
              disabled={cart.length === 0}
            >
              🗑 Vaciar (F3)
            </button>

            <button
              type="button"
              onClick={() => barcodeInputRef.current?.focus()}
            >
              ⌕ Buscar producto
            </button>
          </div>
        </aside>
      </div>

      <section className="recent-sales">
        <div className="recent-sales-heading">
          <div>
            <h2>Últimas ventas</h2>
            <p>Movimientos registrados recientemente.</p>
          </div>

          <button type="button" onClick={loadRecentSales}>
            ↻ Actualizar
          </button>
        </div>

        <div className="recent-sales-table-wrap">
          <table className="recent-sales-table">
            <thead>
              <tr>
                <th>Venta</th>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Pago</th>
                <th>Estado</th>
                <th>Total</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {recentSales.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-sales">
                    Aún no hay ventas registradas.
                  </td>
                </tr>
              ) : (
                recentSales.map((sale) => (
                  <tr key={sale.id}>
                    <td>
                      {sale.document_number || `#${sale.id}`}
                    </td>

                    <td>
                      {sale.created_at
                        ? new Date(sale.created_at).toLocaleString("es-CO")
                        : "-"}
                    </td>

                    <td>{sale.customer_name || "Cliente general"}</td>

                    <td>{getPaymentLabel(sale.payment_method)}</td>

                    <td>
                      <span className={`sale-status sale-status-${sale.status}`}>
                        {getSaleStatusLabel(sale.status)}
                      </span>
                    </td>

                    <td>
                      <strong>{money(sale.total)}</strong>
                    </td>

                    <td className="sale-actions">
                      <button
                        type="button"
                        className="btn-sale-detail"
                        onClick={() => openSaleModal(sale.id, "detail")}
                      >
                        Ver
                      </button>

                      {sale.status !== "cancelled" && (
                        <>
                          {canCancelSales && (
                            <>
                              <button
                                type="button"
                                className="btn-sale-return"
                                onClick={() =>
                                  openSaleModal(sale.id, "return")
                                }
                              >
                                Reintegrar
                              </button>

                              <button
                                type="button"
                                className="btn-sale-cancel"
                                onClick={() =>
                                  openSaleModal(sale.id, "cancel")
                                }
                              >
                                Anular
                              </button>
                            </>
                          )}

                          {canViewAccounting && (
                            <button
                              type="button"
                              className="sale-action-btn sale-action-accounting"
                              onClick={() => handleViewJournalEntry(sale)}
                            >
                              Asiento
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
      {canCancelSales && saleModal && (
        <SaleReturnModal
          sale={saleModal.sale}
          mode={saleModal.mode}
          onClose={() => setSaleModal(null)}
          onCompleted={async (successMessage) => {
            setSaleModal(null);

            setMessage({
              type: "success",
              text: successMessage,
            });

            await Promise.all([
              loadProducts(),
              loadRecentSales(),
              loadCashSession(),
            ]);
          }}
        />
      )}
      {linkedJournalEntry && (
        <div
          className="sale-linked-entry-backdrop"
          onClick={() => setLinkedJournalEntry(null)}
        >
          <section
            className="sale-linked-entry-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>Comprobante contable asociado</h3>

            <p>
              <strong>Número:</strong>{" "}
              {linkedJournalEntry.number}
            </p>

            <p>
              <strong>Fecha:</strong>{" "}
              {new Date(
                `${linkedJournalEntry.entry_date}T00:00:00`
              ).toLocaleDateString("es-CO")}
            </p>

            <p>
              <strong>Descripción:</strong>{" "}
              {linkedJournalEntry.description}
            </p>

            <p>
              <strong>Estado:</strong>{" "}
              {linkedJournalEntry.status}
            </p>

            <div className="sale-linked-entry-actions">
              {canViewAudit && (
                <button
                  type="button"
                  className="sale-action-btn sale-action-accounting"
                  onClick={() =>
                    setAuditJournalEntry(linkedJournalEntry)
                  }
                >
                  Auditoría
                </button>
              )}

              {canViewAccounting && (
                <button
                  type="button"
                  className="sale-action-btn sale-action-accounting"
                  onClick={() =>
                    navigate(`/${company}/contabilidad`)
                  }
                >
                  Abrir Contabilidad
                </button>
              )}
              <button
                type="button"
                className="sale-action-btn"
                onClick={() => setLinkedJournalEntry(null)}
              >
                Cerrar
              </button>
            </div>
          </section>
        </div>
      )}
      {auditJournalEntry && (
        <JournalAuditModal
          journalEntry={auditJournalEntry}
          onClose={() => setAuditJournalEntry(null)}
        />
      )}

      {variantPicker && (
        <div
          className="variant-picker-overlay"
          onClick={() => setVariantPicker(null)}
        >
          <section
            className="variant-picker-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="variant-picker-header">
              <div>
                <span>SELECCIONAR VARIANTE</span>
                <h2>{variantPicker.product.name}</h2>
                <p>
                  Selecciona la presentación que deseas agregar
                  a la venta.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setVariantPicker(null)}
                aria-label="Cerrar selector de variantes"
              >
                ×
              </button>
            </div>

            <div className="variant-picker-list">
              {variantPicker.variants.map((variant) => (
                <button
                  type="button"
                  key={variant.id}
                  className="variant-picker-option"
                  disabled={Number(variant.stock || 0) <= 0}
                  onClick={() => {
                    addToCart(
                      variantPicker.product,
                      variant
                    );

                    setVariantPicker(null);
                  }}
                >
                  <div>
                    <strong>{variant.name}</strong>
                    <small>{variant.sku}</small>
                  </div>

                  <div>
                    <b>
                      {money(
                        getVariantPrice(
                          variantPicker.product,
                          variant
                        )
                      )}
                    </b>

                    <span>
                      Stock: {Number(variant.stock || 0)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default CompanySales;